import { ExternalLink } from "lucide-react";
import { Link } from "react-router";

import { ButtonLink } from "@/components/button-link";
import { AsyncBoundary } from "@/components/data-state";
import { Field } from "@/components/form-field";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useAsync } from "@/hooks/use-async";
import { useAdminKey } from "@/hooks/use-auth";
import { ClientEditFields } from "@/legacy/client-edit-fields";
import type { Maze, PatchChallengeInput } from "@/lib/api";
import { adminApi } from "@/lib/api";

import { SectionCard } from "./section-card";
import { useChallenge, useChallengeState } from "./use-challenge";
import { WorkerCard } from "./worker-card";

/**
 * Where the challenge runs: which maze, whether students may see it, and -- for a
 * server-simulated one -- the worker that serves it.
 *
 * The maze itself (generation, routes, dataset) is managed in the library, because one
 * maze may serve several challenges and may exist before any of them. This section only
 * chooses one, and the server freezes the choice once anyone has run the challenge.
 */
export function AdminChallengeMaze() {
  const { challenge, refetch } = useChallenge();
  const adminKey = useAdminKey();
  const onServer = challenge.simulator === "server";

  const mazes = useAsync(
    adminKey && onServer ? () => adminApi.mazes(adminKey) : null,
    [adminKey, onServer, challenge.bundle_id],
  );
  const sessions = useAsync(
    adminKey && onServer ? () => adminApi.adminSimSessions(challenge.id, adminKey) : null,
    [adminKey, onServer, challenge.id],
  );
  const frozen = (sessions.data?.length ?? 0) > 0;

  const [form, set, reset] = useChallengeState((c) => ({
    bundle_id: c.bundle_id,
    maze_id: c.maze_id,
    exploration_data_url: c.exploration_data_url,
    show_trajectory: c.show_trajectory,
  }));

  const patch: PatchChallengeInput = {};
  if (onServer) {
    if (form.bundle_id !== challenge.bundle_id) patch.bundle_id = form.bundle_id;
  } else if (form.maze_id.trim() !== challenge.maze_id) {
    patch.maze_id = form.maze_id.trim();
  }
  if (form.exploration_data_url.trim() !== challenge.exploration_data_url)
    patch.exploration_data_url = form.exploration_data_url.trim();
  if (form.show_trajectory !== challenge.show_trajectory) patch.show_trajectory = form.show_trajectory;

  const chosen = mazes.data?.find((m) => m.id === challenge.bundle_id) ?? null;

  return (
    <div className="grid gap-4">
      <SectionCard
        title="Maze"
        description={
          onServer
            ? frozen
              ? "Frozen: the challenge has been attempted, and a score is meaningless without the scene it was earned in."
              : "Which library maze this challenge runs in. Changeable until the first attempt."
            : "The maze file students download, and their dataset. The encrypted file itself is fixed: every recording is checked against its md5."
        }
        patch={patch}
        onSaved={refetch}
        onReset={reset}
      >
        {onServer ? (
          <AsyncBoundary state={mazes}>
            {(library) => (
              <MazePicker
                library={library}
                value={form.bundle_id}
                onChange={(id) => set((f) => ({ ...f, bundle_id: id }))}
                frozen={frozen}
                chosen={chosen}
                challengeId={challenge.id}
              />
            )}
          </AsyncBoundary>
        ) : (
          <ClientEditFields
            mazeId={form.maze_id}
            explorationDataUrl={form.exploration_data_url}
            onMazeId={(v) => set((f) => ({ ...f, maze_id: v }))}
            onExplorationDataUrl={(v) => set((f) => ({ ...f, exploration_data_url: v }))}
          />
        )}

        {onServer ? (
          <Field
            id="m-data"
            label="Exploration dataset link"
            hint="Filled in automatically when a dataset is recorded on the maze; a hand-typed link is left alone."
          >
            <Input
              id="m-data"
              value={form.exploration_data_url}
              onChange={(e) => set((f) => ({ ...f, exploration_data_url: e.target.value }))}
              className="font-mono text-xs"
            />
          </Field>
        ) : null}

        <div className="flex items-start gap-3 rounded-md border p-3">
          <Switch
            id="m-traj"
            checked={form.show_trajectory}
            onCheckedChange={(v) => set((f) => ({ ...f, show_trajectory: v }))}
            className="mt-0.5"
          />
          <div>
            <Label htmlFor="m-traj">Reveal walls and trajectories</Label>
            <p className="text-muted-foreground text-xs">
              Lets students replay their runs over the maze. Keep off during a live graded
              challenge — it discloses the layout.
            </p>
          </div>
        </div>
      </SectionCard>

      {onServer && adminKey ? (
        <WorkerCard challengeId={challenge.id} adminKey={adminKey} maze={chosen} />
      ) : null}
    </div>
  );
}

function MazePicker({
  library,
  value,
  onChange,
  frozen,
  chosen,
  challengeId,
}: {
  library: Maze[];
  value: string | null;
  onChange: (id: string | null) => void;
  frozen: boolean;
  chosen: Maze | null;
  challengeId: string;
}) {
  if (library.length === 0) {
    return (
      <p className="text-sm">
        The library is empty.{" "}
        <Link to="/admin/mazes" className="underline underline-offset-4">
          Generate a maze
        </Link>{" "}
        first.
      </p>
    );
  }
  const items = Object.fromEntries(library.map((m) => [m.id, m.name]));
  return (
    <div className="grid gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <Select items={items} value={value ?? ""} onValueChange={(v) => onChange(v || null)} disabled={frozen}>
          <SelectTrigger className="w-full sm:w-96">
            <SelectValue placeholder="Choose a maze…" />
          </SelectTrigger>
          <SelectContent>
            {library.map((m) => (
              <SelectItem key={m.id} value={m.id}>
                {m.name}
                <span className="text-muted-foreground ml-2 font-mono text-xs">
                  {m.grid ? `${m.grid.rows}×${m.grid.cols}` : ""} ·{" "}
                  {m.dataset_state === "done" ? "dataset" : "no dataset"}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <ButtonLink variant="ghost" size="sm" to="/admin/mazes">
          Library
          <ExternalLink className="size-3 opacity-60" aria-hidden />
        </ButtonLink>
      </div>

      {chosen ? (
        <dl className="grid grid-cols-[8rem_1fr] gap-1 rounded-md border p-3 text-xs">
          <dt className="text-muted-foreground">Maze</dt>
          <dd>
            <Link to={`/admin/mazes/${chosen.id}`} className="font-medium hover:underline">
              {chosen.name}
            </Link>
            <span className="text-muted-foreground ml-2 font-mono">{chosen.id}</span>
          </dd>
          <dt className="text-muted-foreground">Grid · camera</dt>
          <dd className="font-mono">
            {chosen.grid?.rows}×{chosen.grid?.cols} · {chosen.camera?.width}×{chosen.camera?.height}
          </dd>
          {chosen.shortest_path ? (
            <>
              <dt className="text-muted-foreground">Shortest route</dt>
              <dd className="font-mono">
                {chosen.shortest_path.metres.toFixed(1)} m · {chosen.shortest_path.oracle_steps.toLocaleString()} oracle steps
                <span className="text-muted-foreground font-sans"> — what students' path ratios divide by</span>
              </dd>
            </>
          ) : null}
          <dt className="text-muted-foreground">Dataset</dt>
          <dd>
            {chosen.dataset_state === "done" ? (
              "recorded; students download it from the challenge page"
            ) : (
              <span className="text-destructive">
                none —{" "}
                <Link to={`/admin/mazes/${chosen.id}`} className="underline">
                  record one
                </Link>{" "}
                or students have nothing to learn from
              </span>
            )}
          </dd>
          {chosen.used_by.length > 1 ? (
            <>
              <dt className="text-muted-foreground">Shared with</dt>
              <dd>{chosen.used_by.filter((u) => u.id !== challengeId).map((u) => u.name).join(", ")}</dd>
            </>
          ) : null}
        </dl>
      ) : null}
    </div>
  );
}
